import {
  MediaTimelineViewportController,
  type MediaTimelineViewportSnapshot,
} from "@carbon6/c6-frontend-design";
import { useSyncExternalStore } from "react";

/** 재생 바가 지금 그리는 시간 창. 창의 소유자는 항상 c6 바다. 이 값은 그 결과다. */
export type PlayerViewport = Pick<MediaTimelineViewportSnapshot, "startSeconds" | "endSeconds">;

/**
 * 창을 구독하는 핸들. `transport` 와 같은 모양이라 소비처가 같은 방식으로 읽는다.
 *
 * state 대신 구독을 쓰는 이유: 창은 타임라인을 끌거나 가장자리를 누르는 동안 **매 프레임**
 * 바뀐다. 이 값을 영상 컴포넌트 위의 state 로 가지면 프레임마다 카메라 그리드까지 다시
 * 조정된다. identity 가 고정된 핸들로 전달하면 값을 실제로 쓰는 컴포넌트만 다시 그린다.
 */
export type PlayerViewportSource = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => PlayerViewport | null;
};

/**
 * 행 타임라인의 창을 보관하는 c6 store. 재생 바에 넘기면 바가 이 store 로 창을 움직인다.
 * 소비처는 같은 store 를 `PlayerViewportSource` 로 구독한다.
 *
 * 바가 붙어 있지 않으면 스냅샷은 null 이다. 가벼운 재생 바(행 없음)와 채널 0개가 그 경우다.
 * 그래서 "창 없음"을 따로 알리는 코드가 필요 없다.
 */
export type PlayerTimelineViewport = MediaTimelineViewportController;

/**
 * 행 타임라인의 창 store 를 만든다. 기준 창은 에피소드 전체 길이(`fit`)다.
 *
 * c6 기본 기준은 20초 창이다. 창원 실측에서 에피소드 길이의 중앙값은 22~26초다. 20초 창으로 열면
 * 에피소드 뒷부분이 잘린다. 그러면 행에 표시한 구간이 있는지도 보이지 않는다.
 */
export function createPlayerTimelineViewport(): PlayerTimelineViewport {
  return new MediaTimelineViewportController({ baseline: "fit" });
}

/** 창이 없는 자리(영상 없음·테스트)에서 구독을 만들지 않게 하는 빈 소스. */
const IDLE_SOURCE: PlayerViewportSource = {
  subscribe: () => () => {},
  getSnapshot: () => null,
};

/** 창을 구독해 값으로 읽는다. `null` 소스는 구독하지 않고 "창 없음"을 반환한다. */
export function usePlayerViewport(source: PlayerViewportSource | null): PlayerViewport | null {
  const resolved = source ?? IDLE_SOURCE;
  return useSyncExternalStore(resolved.subscribe, resolved.getSnapshot);
}
